/**
 * Company-wide timezone.
 *
 * Shariti is a French company running immersions physically in France, so
 * every human-facing date/time in Prerec is expressed in Europe/Paris,
 * regardless of where the connected user is located (Sarah works from
 * Cotonou, Benin — she still schedules immersions in Paris local time).
 *
 * All datetimes are persisted in UTC (ISO strings). The helpers below
 * project them to Europe/Paris wall clock at render time, and convert
 * user-entered Paris wall clocks back to UTC at write time.
 */
export const COMPANY_TZ = "Europe/Paris";
export const COMPANY_TZ_LABEL = "Heure de Paris";

/**
 * Extract the Europe/Paris wall clock components of a UTC instant.
 * Returns { year, month(1-12), day, hour(0-23), minute, second }.
 */
export function parisParts(dateOrIso) {
    const d = typeof dateOrIso === "string" ? new Date(dateOrIso) : dateOrIso;
    if (!d || isNaN(d.getTime?.())) return null;
    const raw = new Intl.DateTimeFormat("en-US", {
        timeZone: COMPANY_TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).formatToParts(d);
    const parts = {};
    for (const p of raw) parts[p.type] = p.value;
    let hour = parseInt(parts.hour, 10);
    if (hour === 24) hour = 0; // Node/Chrome sometimes returns 24 for midnight
    return {
        year: parseInt(parts.year, 10),
        month: parseInt(parts.month, 10),
        day: parseInt(parts.day, 10),
        hour,
        minute: parseInt(parts.minute, 10),
        second: parseInt(parts.second, 10),
    };
}

/**
 * Convert a Europe/Paris wall clock (year, month 1-12, day, hour, minute)
 * to a real UTC Date instance. Handles DST correctly — a naive
 * `Date.UTC(...)` would be off by the Paris offset (1 or 2 hours).
 */
export function parisWallToUtc(year, month, day, hour, minute) {
    // Start with the "wall clock as if UTC" — this is our target Paris time
    // expressed as a UTC instant. Compute how far off that guess is from the
    // real Paris wall clock we want, then correct once.
    const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    const projected = parisParts(guess);
    const projectedUtc = Date.UTC(
        projected.year,
        projected.month - 1,
        projected.day,
        projected.hour,
        projected.minute,
        0,
    );
    const targetUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
    const diff = projectedUtc - targetUtc;
    return new Date(guess.getTime() - diff);
}

/**
 * Format a UTC ISO / Date in Europe/Paris using Intl.DateTimeFormat.
 * Automatically injects `timeZone: 'Europe/Paris'` so the browser's local
 * timezone never leaks into the rendered string.
 */
export function formatInParis(dateOrIso, options = {}) {
    if (!dateOrIso) return "—";
    try {
        const d = typeof dateOrIso === "string" ? new Date(dateOrIso) : dateOrIso;
        return new Intl.DateTimeFormat("fr-FR", {
            timeZone: COMPANY_TZ,
            ...options,
        }).format(d);
    } catch {
        return "—";
    }
}

/**
 * Detect whether the connected user's browser resolves times in a timezone
 * OTHER than Europe/Paris — used to display a discreet "Heure de Paris ·
 * chez vous : 15:19" hint next to date pickers so remote pre-recruteurs
 * (Cotonou, Dakar, Casablanca…) don't wonder why the number they type
 * doesn't match their laptop clock.
 */
export function browserTz() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}

export function isRemoteTz() {
    return browserTz() && browserTz() !== COMPANY_TZ;
}

/**
 * Given a Paris wall clock "HH:MM" today, return the equivalent wall clock
 * "HH:MM" in the user's browser timezone. Used for the "chez vous" hint.
 */
export function parisTimeToBrowserWall(hh, mm) {
    const now = new Date();
    const p = parisParts(now);
    if (!p) return null;
    // Build a Paris wall-clock for TODAY at hh:mm, project it back to browser tz.
    const asUtc = parisWallToUtc(p.year, p.month, p.day, hh, mm);
    return new Intl.DateTimeFormat("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(asUtc);
}
